import { useEffect, useState, type ReactElement } from 'react';
import { ApiError } from '../shared/api/client';
import { api } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { formatPhoneInput, isPhoneComplete, phoneFromHash, phoneToE164 } from '../shared/lib/phone';
import { primaryButtonStyle } from '../shared/ui/primitives';
import { Logo } from '../shared/ui/Logo';

/**
 * Вход в кабинет (NFR-922).
 *
 * Причина отказа не уточняется: по разнице между «нет такого номера» и «неверный
 * пароль» подбирается и то, и другое. Исключение — превышение числа попыток: его
 * показать нужно, иначе диспетчер будет думать, что забыл пароль, и менять его.
 */
export function LoginPage({ onSuccess }: { readonly onSuccess: () => void }): ReactElement {
  const messages = useMessages();
  const [phone, setPhone] = useState(
    () => phoneFromHash(window.location.hash) ?? formatPhoneInput(''),
  );

  // Номер от соседней инсталляции подставлен — из адресной строки его убираем.
  useEffect(() => {
    if (window.location.hash !== '') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setPending(true);
    setError(null);

    try {
      await api.login(phoneToE164(phone), password);
      onSuccess();
    } catch (cause) {
      const locked = cause instanceof ApiError && cause.code === 'too_many_attempts';
      setError(locked ? messages.cabinet.auth.locked : messages.cabinet.auth.failed);
    } finally {
      setPending(false);
    }
  };

  const label = {
    fontSize: 'var(--fs-sm)',
    fontWeight: 'var(--fw-semibold)',
    color: 'var(--text-2)',
  } as const;

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        padding: 'var(--sp-8) var(--sp-4)',
        background: 'var(--bg)',
      }}
    >
      <div style={{ width: 'min(380px, 100%)', margin: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--sp-4)' }}>
          <Logo name={messages.cabinet.common.brand} />
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          style={{
            display: 'grid',
            gap: 'var(--sp-3)',
            padding: 'var(--sp-6) var(--sp-4)',
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-lg)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <div>
            <h1 style={{ fontSize: 'var(--fs-title)' }}>{messages.cabinet.auth.title}</h1>
            <p className="caption" style={{ margin: 'var(--sp-1) 0 0' }}>
              {messages.cabinet.auth.lead}
            </p>
          </div>

          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span style={label}>{messages.cabinet.auth.phone}</span>
            <input
              autoComplete="username"
              inputMode="tel"
              onChange={(event) => setPhone(formatPhoneInput(event.target.value))}
              placeholder={messages.cabinet.auth.phoneExample}
              value={phone}
            />
          </label>

          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span style={label}>{messages.cabinet.auth.password}</span>
            <input
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
              type="password"
              value={password}
            />
          </label>

          {error === null ? null : (
            <span
              role="status"
              style={{
                padding: 'var(--sp-2) var(--sp-3)',
                borderRadius: 'var(--r-sm)',
                border: '1px solid var(--danger-line)',
                background: 'var(--danger-soft)',
                color: 'var(--danger)',
              }}
            >
              {error}
            </span>
          )}

          <button
            type="submit"
            disabled={pending || !isPhoneComplete(phone)}
            style={{
              ...primaryButtonStyle,
              width: '100%',
              height: 'var(--field-h-lg)',
              marginTop: 'var(--sp-1)',
              opacity: pending || !isPhoneComplete(phone) ? 0.5 : 1,
            }}
          >
            {messages.cabinet.auth.submit}
          </button>
        </form>

        <p
          className="caption"
          style={{ textAlign: 'center', margin: 'var(--sp-3) 0 0', fontSize: 'var(--fs-xs)' }}
        >
          {messages.cabinet.auth.note}
        </p>
      </div>
    </main>
  );
}
